import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { LoginRequest } from '../../../models/auth.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-login',
  imports: [FormsModule],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  device_id: string = '';
  user_agent: string =
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36';
  latitude: number = 0;
  longitude: number = 0;
  email: string = '';
  contra: string = '';
  otp: string = '';

  warningText: string = '';
  successText: string = '';
  loading: boolean = false;
  paso: 'credenciales' | 'otp' = 'credenciales';

  // 2FA desactivado temporalmente hasta reparar el envío del código por correo.
  readonly otpHabilitado: boolean = environment.superAdminOtpEnabled;

  constructor(
    private router: Router,
    private authService: AuthService,
    private http: HttpClient,
  ) {}

  ngOnInit() {
    this.http
      .get<{ ip: string }>('https://api.ipify.org?format=json')
      .subscribe((data) => {
        this.device_id = data.ip;
      });

    this.user_agent = navigator.userAgent;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearMessages() {
    this.warningText = '';
    this.successText = '';
  }

  // Inicia sesión: con 2FA solicita el código, sin 2FA entra directo.
  iniciarSesion() {
    this.clearMessages();

    if (!this.validateCredentials()) return;

    this.loading = true;

    const loginData: LoginRequest = {
      device_id: this.device_id,
      user_agent: this.user_agent,
      latitude: this.latitude,
      longitude: this.longitude,
      email: this.email,
      contra: this.contra,
    };

    if (!this.otpHabilitado) {
      this.authService
        .login(loginData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.redirigirAlPanel(),
          error: (error) => {
            this.warningText = error.message;
            this.loading = false;
          },
        });
      return;
    }

    this.authService
      .solicitarOtp(loginData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.successText = response.message || 'Código enviado a tu correo';
          this.paso = 'otp';
          this.otp = '';
          this.loading = false;
        },
        error: (error) => {
          this.warningText = error.message;
          this.loading = false;
        },
      });
  }

  // Paso 2: verificar OTP
  verificarOtp() {
    this.clearMessages();

    if (!this.otp || this.otp.length !== 6) {
      this.warningText = 'Ingresa el código de 6 dígitos';
      return;
    }

    this.loading = true;

    this.authService
      .verificarOtp({ email: this.email, otp: this.otp })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.redirigirAlPanel(),
        error: (error) => {
          this.warningText = error.message;
          this.loading = false;
        },
      });
  }

  private redirigirAlPanel() {
    this.successText = '¡Bienvenido! Redirigiendo...';
    this.router.navigate(['/dashboard/empresas']).then((navigated) => {
      if (!navigated) {
        this.warningText = 'No se pudo completar la redirección.';
        this.successText = '';
      }
      this.loading = false;
    });
  }

  // Volver al paso de credenciales
  volverACredenciales() {
    this.paso = 'credenciales';
    this.otp = '';
    this.clearMessages();
  }

  private validateCredentials(): boolean {
    if (!this.email || !this.contra) {
      this.warningText = 'Por favor, completa todos los campos.';
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(this.email)) {
      this.warningText = 'El correo electrónico no tiene un formato válido.';
      return false;
    }

    if (this.contra.length < 6) {
      this.warningText = 'La contraseña debe tener al menos 6 caracteres.';
      return false;
    }

    return true;
  }
}
